import { Box, Typography } from '@mui/material';
import { TextBlock } from '../TextBlock';
import type { EvaluationCaseProps } from './types';

/**
 * One evaluation case: what goes in, and what a good result looks like.
 *
 * **The output is labelled "Expected output", never "Output".** Nothing runs
 * these cases yet, so there is no output to show — what the record holds is the
 * author's description of a good one, and calling it a result would claim a run
 * that never happened. When a runner exists and stores real outputs, they arrive
 * as a second block beside this one and not as a rename of it.
 */

/** A small label over a block, the same wherever one appears. */
const Labelled = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Typography variant="overline" component="h4" sx={{ color: 'text.secondary', letterSpacing: '1px' }}>
      {label}
    </Typography>
    {children}
  </Box>
);

export const EvaluationCase = ({ evaluation }: EvaluationCaseProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <Box>
      <Typography variant="h4" component="h3">
        {evaluation.name}
      </Typography>

      {evaluation.fixtures.length > 0 && (
        <Typography variant="body2" sx={{ color: 'text.meta', fontSize: 12, marginTop: 1 }}>
          Runs against {evaluation.fixtures.join(', ')}
        </Typography>
      )}
    </Box>

    <Labelled label="Input">
      <TextBlock label={`Input for ${evaluation.name}`}>{evaluation.input}</TextBlock>
    </Labelled>

    <Labelled label="Expected output">
      <TextBlock label={`Expected output for ${evaluation.name}`}>
        {evaluation.expectedOutput}
      </TextBlock>
    </Labelled>

    {evaluation.expectations.length > 0 && (
      <Labelled label="A good output">
        <Box component="ul" sx={{ margin: 0, paddingLeft: 5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {evaluation.expectations.map((expectation) => (
            <Typography key={expectation} component="li" variant="body2" sx={{ color: 'text.secondary' }}>
              {expectation}
            </Typography>
          ))}
        </Box>
      </Labelled>
    )}
  </Box>
);
